// Scrivener import in the browser (`design-docs/Scrivener import in the browser — design.md`): the package from a .zip
// or a folder picker, imported in this browser onto a new project's documents (`@writing-suite/core/import`) and put on
// the relay as any new project is (`createProject` in planNewProject.ts), or re-imported three-way into the open project
// through its live edit, as File ▸ Import ▸ Scrivener Project… does on a Mac. Scrivener's media files and inline images
// go up as the relay's `attachment.<hmac>` snapshots (PROTOCOL.md §2.3), sealed with the project key, as the Apple
// apps send theirs. Nothing of the package leaves the browser but the sealed documents and files.
import * as A from '@automerge/automerge';
import { unzipSync } from 'fflate';
import type { Api } from '@writing-suite/client/net/api.ts';
import { SyncConnection } from '@writing-suite/client/net/sync.ts';
import { fromHex } from '@writing-suite/client/shared/b64.ts';
import { attachmentDoc, seal } from '@writing-suite/client/shared/crypto.ts';
import {
  arrangementTree, DocumentEditor, readProject, StoreEditor, type ArrangementTreeNode, type HistorySummary, type ProjectEditor, type ProjectType,
} from '@writing-suite/core';
import {
  fileMapFrom, importScrivenerPackage, importScrivenerPackageSync, ScrivenerError, ScrivenerPackage,
  type ImportedAttachment, type ScrivenerImportReport,
} from '@writing-suite/core/import';
import type { NewProjectDoc } from './planNewProject.ts';
import type { LiveProject } from './project.ts';

const NOT_A_PROJECT = 'That isn’t a Scrivener project: no .scrivx file in it.';

/** A `.scriv` package zipped (the folder inside the zip, or its files at the top). */
export function packageFromZip(zip: Uint8Array): ScrivenerPackage {
  let entries: [string, Uint8Array][];
  try {
    entries = Object.entries(unzipSync(zip));
  } catch {
    throw new ScrivenerError('notAScrivenerProject', 'That file isn’t a zip archive. Zip the .scriv folder, or choose the folder itself.');
  }
  return packageFromEntries(entries);
}

/** A `.scriv` folder from a folder picker (`webkitdirectory`): every file with its path inside the folder. */
export async function packageFromFiles(files: ArrayLike<File>): Promise<ScrivenerPackage> {
  const entries: [string, Uint8Array][] = [];
  for (const file of Array.from(files)) {
    const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    entries.push([relative, new Uint8Array(await file.arrayBuffer())]);
  }
  return packageFromEntries(entries);
}

/** A `.scriv` package from its files, named as a zip or a folder picker names them (the folder first, or not). */
export function packageFromEntries(entries: [string, Uint8Array][]): ScrivenerPackage {
  const map = fileMapFrom(entries);
  if (!map) throw new ScrivenerError('notAScrivenerProject', NOT_A_PROJECT);
  return new ScrivenerPackage(map.files, map.title);
}

export interface ImportedProject {
  built: { projectId: string; type: ProjectType; docs: NewProjectDoc[]; opening: string | null };
  report: ScrivenerImportReport;
  attachments: ImportedAttachment[];
}

/** Imports the package as a new project: the documents to put on the relay, the files to upload, and the report.
 *  Yields between items so progress shows. */
export async function importPackage(
  pkg: ScrivenerPackage,
  o: { author: string; locale: string; title?: string; progress?: (done: number, total: number, title: string) => void },
): Promise<ImportedProject> {
  const titled = o.title && o.title !== pkg.title ? new ScrivenerPackage(pkg.files, o.title) : pkg;
  const result = await importScrivenerPackage(titled, {
    author: o.author,
    locale: o.locale,
    progress: o.progress,
    yield: () => new Promise((resolve) => setTimeout(resolve, 0)),
  });
  const project = readProject(result.docs.get('project')!);
  const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
  return {
    built: {
      projectId: result.projectID,
      type: result.type,
      docs: [...result.docs].map(([name, doc]) => ({ name, bytes: A.save(doc) })),
      opening: manuscript ? firstDocument(arrangementTree(manuscript)) : null,
    },
    report: result.report,
    attachments: result.attachments,
  };
}

/** Re-imports the package into the open project (its `source` must be this package's), three-way, in one edit;
 *  `allowOlder` takes a package that looks like an older copy. Returns the report and the files to upload. */
export function reimportPackage(live: LiveProject, pkg: ScrivenerPackage, o: { author: string; allowOlder?: boolean }): { report: ScrivenerImportReport; attachments: ImportedAttachment[] } {
  return live.edit((editor: ProjectEditor) => {
    const d = editor as unknown as DocumentEditor;
    const result = importScrivenerPackageSync(pkg, { author: o.author, docs: d.docs, allowOlder: o.allowOlder, ...(d.context.actor ? { actor: d.context.actor } : {}) });
    return { report: result.report, attachments: result.attachments };
  });
}

/** Statistics ▸ Import Scrivener History…: only the package's writing history (Project ▸ Writing History) into the open
 *  project, in one edit — no checkpoint, no merge (`ScrivenerHistory.importHistory`). */
export function importHistory(live: LiveProject, pkg: ScrivenerPackage): HistorySummary {
  return live.edit((editor: ProjectEditor) => {
    const d = editor as unknown as DocumentEditor;
    return new StoreEditor(d.docs, d.context).importScrivenerHistory(pkg);
  });
}

const HISTORY_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** A `YYYY-MM-DD` day as the writer reads it ("22 Apr 2026"). */
export function historyDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return HISTORY_DATE.format(new Date(y, m - 1, d));
}

/** `ScrivenerHistory.Summary.text`: "11 days, 22 Apr 2026 – 16 Sep 2026: 8,968 words in the draft, 561 elsewhere". */
export function historyText(s: HistorySummary): string {
  if (!s.first || !s.last) return 'No days';
  const span = s.first === s.last ? historyDay(s.first) : `${historyDay(s.first)} – ${historyDay(s.last)}`;
  let words = `${s.draftWords.toLocaleString('en-GB')} words in the draft`;
  if (s.otherWords) words += `, ${s.otherWords.toLocaleString('en-GB')} elsewhere`;
  return `${s.days === 1 ? '1 day' : `${s.days.toLocaleString('en-GB')} days`}, ${span}: ${words}`;
}

/** Puts Scrivener's files on the relay as attachment snapshots (sealed with the project key, named by the HMAC of
 *  their SHA-256). `cursor`: what this device has merged, so the relay doesn't replay the log to this connection. */
export async function uploadAttachments(api: Api, projectId: string, key: Uint8Array, attachments: ImportedAttachment[], cursor = 0): Promise<void> {
  if (!attachments.length) return;
  const conn = new SyncConnection(api, projectId);
  await conn.connect();
  try {
    const { limits } = await conn.hello(cursor);
    for (const a of attachments) {
      const doc = await attachmentDoc(key, fromHex(a.sha256));
      // An attachment is one snapshot, sent once: its cid is its name, so a re-send is a duplicate (as the Mac sends it).
      const r = await conn.append('snapshot', { doc, epoch: 0, lineage: '', cid: doc.slice('attachment.'.length, 'attachment.'.length + 32), heads: '', base: cursor }, await seal(key, a.bytes), limits);
      if (r.t === 'error') throw new Error(`${a.filename}: ${r.message}`);
    }
  } finally {
    conn.close();
  }
}

function firstDocument(nodes: ArrangementTreeNode[]): string | null {
  for (const n of nodes) {
    if (n.node.fragmentID && !n.node.isFolder) return n.node.id;
    const inner = firstDocument(n.children);
    if (inner) return inner;
  }
  return null;
}

/** The report's lines for the dialog (`FolderImportReportView`'s terse style). A Word import's (`structure`) says what was
 *  made of the document first; its links are in the text, its pictures attachments. */
export function reportLines(report: ScrivenerImportReport & { structure?: string[] }): string[] {
  const n = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
  const lines: string[] = [];
  if (report.structure) {
    lines.push(...report.structure);
    lines.push(`${n(report.fragmentsCreated, 'document')}, ${report.totalWords.toLocaleString()} words in the manuscript`);
    const kept: string[] = [];
    if (report.commentsImported) kept.push(`${n(report.commentsImported, 'comment')} in the margin`);
    if (report.linksPreserved) kept.push(n(report.linksPreserved, 'link'));
    if (report.attachmentsStored) kept.push(report.attachmentsStored === 1 ? '1 picture as an attachment' : `${report.attachmentsStored} pictures as attachments`);
    if (kept.length) lines.push(`Kept: ${kept.join(', ')}`);
    const v = report.verification;
    if (v.textMismatches.length) lines.push(`Check: ${v.textMismatches.join('; ')}`);
    else if (v.itemsChecked) lines.push(`Every word arrived (${n(v.itemsChecked, 'document')} checked)`);
    return lines;
  }
  if (report.isReimport) {
    const parts = [n(report.fragmentsUpdated, 'document') + ' updated'];
    if (report.fragmentsCreated) parts.push(n(report.fragmentsCreated, 'document') + ' added');
    if (report.fragmentsRemoved) parts.push(n(report.fragmentsRemoved, 'document') + ' moved to the Trash');
    parts.push(n(report.fragmentsUnchanged, 'document') + ' unchanged');
    lines.push(parts.join(', '));
    if (report.conflicts) lines.push(`${n(report.conflicts, 'document')} changed in both apps: Five Rooms' text kept, Scrivener's offered as a suggestion`);
    for (const name of report.structureConflicts) lines.push(`${name} was reordered in both apps: Five Rooms' order kept, Scrivener's added as an arrangement`);
  } else {
    lines.push(`${n(report.fragmentsCreated, 'document')}, ${report.totalWords.toLocaleString()} words in the manuscript`);
  }
  const kept: string[] = [];
  if (report.commentsImported) kept.push(n(report.commentsImported, 'comment'));
  if (report.linksPreserved) kept.push(n(report.linksPreserved, 'link'));
  if (report.highlightsPreserved) kept.push(n(report.highlightsPreserved, 'highlight'));
  if (kept.length) lines.push(`${kept.join(', ')} kept as margin comments`);
  if (report.snapshotsImported) lines.push(`${n(report.snapshotsImported, 'snapshot')} kept in History`);
  if (report.writingHistory) lines.push(`Writing history: ${historyText(report.writingHistory)}`);
  if (report.attachmentsStored) lines.push(`${n(report.attachmentsStored, 'media file or image', 'media files and images')} kept as attachments`);
  const v = report.verification;
  if (v.textMismatches.length || v.structureMismatches.length) lines.push(`Check: ${[...v.structureMismatches, ...v.textMismatches].slice(0, 5).join('; ')}`);
  else if (v.itemsChecked) lines.push(`Every word arrived (${n(v.itemsChecked, 'document')} checked)`);
  return lines;
}
